import { api, unwrapResponse } from './api';
import { startLoading } from '../utils/loader';
import {
  parseDelimitedProducts,
  parseDelimitedScans,
} from '../utils/delimitedData';

// Keep each request well under nginx's default 1 MB body limit, allowing for
// JSON escaping of tabs and line breaks.
const MAX_PART_BYTES = 600 * 1024;
// Book stock rows are ~150 bytes as JSON, so 3,000 rows ≈ 450 KB per request.
const BOOK_ROWS_PER_PART = 3000;

/**
 * Backdrop for an upload sent in parts: "Uploading master file…" with
 * "Part 2 of 5 · 12,000 rows" underneath. Returns { part(i), done() }.
 */
function uploadLoader(what, parts, rows) {
  const rowsText = rows ? ` · ${Number(rows).toLocaleString()} rows` : '';
  const task = startLoading(`Uploading ${what}…`, { detail: parts > 1 ? `Part 1 of ${parts}${rowsText}` : rowsText.slice(3) });
  return {
    part: (index) => parts > 1 && task.update(null, `Part ${Math.min(index + 1, parts)} of ${parts}${rowsText}`),
    done: () => task.done(),
  };
}
// Location rows are ~80 bytes as JSON, so 5,000 rows ≈ 400 KB per request.
const LOCATION_ROWS_PER_PART = 5000;

function newUploadId() {
  return (
    Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
  ).slice(0, 16);
}

/** Splits text at line breaks into parts of at most MAX_PART_BYTES (UTF-8). */
function splitIntoParts(text) {
  const encoder = new TextEncoder();
  const lines = text.split('\n');
  const parts = [];
  let current = [];
  let currentBytes = 0;
  let lineOffset = 0;

  lines.forEach((line, index) => {
    const bytes = encoder.encode(line).length + 1;
    if (current.length && currentBytes + bytes > MAX_PART_BYTES) {
      parts.push({ text: current.join('\n'), lineOffset });
      lineOffset = index;
      current = [];
      currentBytes = 0;
    }
    current.push(line);
    currentBytes += bytes;
  });
  parts.push({ text: current.join('\n'), lineOffset });
  return parts;
}

export const auditService = {
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    const qs = query.toString();
    return api.get(`/audits${qs ? `?${qs}` : ''}`).then(unwrapResponse);
  },
  getById: (id) => api.get(`/audits/${id}`).then(unwrapResponse),
  create: (payload) => api.post('/audits', payload).then(unwrapResponse),
  update: (id, payload) =>
    api.put(`/audits/${id}`, payload).then(unwrapResponse),
  delete: (id) => api.delete(`/audits/${id}`),
  getTeams: (id) => api.get(`/audits/${id}/teams`).then(unwrapResponse),
  updateTeams: (id, payload) =>
    api.put(`/audits/${id}/teams`, payload).then(unwrapResponse),
  getMasterFile: (id) =>
    api.get(`/audits/${id}/master-file`).then(unwrapResponse),
  /**
   * Sends the master file in parts small enough for the web server's request
   * size limit (nginx allows about 1 MB). Parts are split at line breaks so no
   * row is cut; the server swaps the new master in when the last part arrives.
   * onProgress({ part, parts }) is called after each part.
   */
  uploadMasterFile: async (id, payload, onProgress) => {
    const parts = splitIntoParts(payload.data || '');
    const uploadId = newUploadId();
    let skippedLines = 0;
    let warnings = [];
    let result = null;
    const loader = uploadLoader('master file', parts.length, (payload.data || '').split('\n').length);

    try {
      for (let index = 0; index < parts.length; index += 1) {
        loader.part(index);
        const response = await api
          .post(`/audits/${id}/master-file`, {
            upload_id: uploadId,
            part_index: index,
            part_count: parts.length,
            line_offset: parts[index].lineOffset,
            data: parts[index].text,
            filename: payload.filename,
          })
          .then(unwrapResponse);

        skippedLines += response?.skipped_lines || 0;
        warnings = warnings.concat(response?.warnings || []).slice(0, 10);
        result = response;
        if (onProgress) onProgress({ part: index + 1, parts: parts.length });
      }
    } finally {
      loader.done();
    }

    return { ...result, skipped_lines: skippedLines, warnings };
  },
  getProducts: async (id, params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);
    const qs = query.toString();
    const response = await api.get(`/audits/${id}/products${qs ? `?${qs}` : ''}`);
    const payload = unwrapResponse(response) || {};

    return {
      ...payload,
      items: parseDelimitedProducts(payload.data),
    };
  },
  getScannerProducts: (id) =>
    api.get(`/audits/${id}/scanner-products`).then(unwrapResponse),
  getScans: async (id, params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.location) query.set('location', params.location);
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);
    const qs = query.toString();
    const response = await api.get(`/audits/${id}/scans${qs ? `?${qs}` : ''}`);
    const payload = unwrapResponse(response) || {};

    return {
      ...payload,
      items: parseDelimitedScans(payload.data),
    };
  },
  recordScans: (id, payload) =>
    api.post(`/audits/${id}/scans`, {
      data: payload.data,
      scanned_by_name: payload.scanned_by_name,
    }).then(unwrapResponse),
  getRoster: (id) => api.get(`/audits/${id}/roster`).then(unwrapResponse),
  getMemberOverrides: (id) =>
    api.get(`/audits/${id}/member-overrides`).then(unwrapResponse),
  updateMemberOverrides: (id, overrides) =>
    api.put(`/audits/${id}/member-overrides`, { overrides }).then(unwrapResponse),
  getMemberConflicts: (id) =>
    api.get(`/audits/${id}/member-conflicts`).then(unwrapResponse),
  previewConflicts: (payload) =>
    api.post('/audits/preview-conflicts', payload, { silent: true }).then(unwrapResponse),

  /** Audits the logged-in client user may view. */
  getClientAudits: () => api.get('/audits/client/mine').then(unwrapResponse),

  /** mode: 'book' | 'master' | 'people' (people also returns days). */
  getComparison: (id, mode = 'book') => {
    const query = new URLSearchParams({ mode });
    if (mode === 'people') {
      try {
        query.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata');
      } catch {
        query.set('tz', 'Asia/Kolkata');
      }
    }
    return api.get(`/audits/${id}/comparison?${query}`).then(unwrapResponse);
  },

  // ---- Live audit features ------------------------------------------------

  /** Summary cards: scans, progress, zones, discrepancies, recounts, sign-offs. */
  getOverview: (id) => api.get(`/audits/${id}/overview`).then(unwrapResponse),

  getLocations: (id) => api.get(`/audits/${id}/locations`).then(unwrapResponse),

  deleteLocations: (id) => api.delete(`/audits/${id}/locations`),

  /** Location batches (by zone) with progress and the schedule's members. */
  getLocationBatches: (id, { locations = false } = {}) =>
    api.get(`/audits/${id}/location-batches${locations ? '?locations=1' : ''}`).then(unwrapResponse),
  generateLocationBatches: (id, batchSize) =>
    api
      .post(`/audits/${id}/location-batches/generate`, { batch_size: batchSize }, { loadingMessage: 'Rebuilding batches…' })
      .then(unwrapResponse),
  assignLocationBatch: (id, batchId, userId) =>
    api.put(`/audits/${id}/location-batches/${batchId}`, { assigned_user_id: userId }).then(unwrapResponse),

  /** rows: [{ location_code, location_name, zone }], sent in parts. */
  uploadLocations: async (id, { rows, filename }, onProgress) => {
    const uploadId = newUploadId();
    const parts = Math.max(1, Math.ceil(rows.length / LOCATION_ROWS_PER_PART));
    let result = null;
    let skipped = 0;
    const loader = uploadLoader('location master', parts, rows.length);
    try {
      for (let index = 0; index < parts; index += 1) {
        loader.part(index);
        result = await api
          .post(`/audits/${id}/locations`, {
            upload_id: uploadId,
            part_index: index,
            part_count: parts,
            filename,
            rows: rows.slice(index * LOCATION_ROWS_PER_PART, (index + 1) * LOCATION_ROWS_PER_PART),
          })
          .then(unwrapResponse);
        skipped += result?.rows_skipped || 0;
        if (onProgress) onProgress({ part: index + 1, parts });
      }
    } finally {
      loader.done();
    }
    return { ...result, rows_skipped: skipped };
  },

  /** params: search, action, type, sort ('qty' | 'value'), limit, offset, all. */
  getDiscrepancies: (id, params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    const qs = query.toString();
    return api.get(`/audits/${id}/discrepancies${qs ? `?${qs}` : ''}`).then(unwrapResponse);
  },

  setDiscrepancyLimits: (id, limits) =>
    api.put(`/audits/${id}/discrepancy-limits`, limits).then(unwrapResponse),

  getRecounts: (id) => api.get(`/audits/${id}/recounts`).then(unwrapResponse),

  createRecount: (id, payload) =>
    api.post(`/audits/${id}/recounts`, payload).then(unwrapResponse),

  updateRecount: (id, requestId, payload) =>
    api.put(`/audits/${id}/recounts/${requestId}`, payload).then(unwrapResponse),

  // Invalid-EAN correction ("Valid EAN" tab).
  // Manual adjustments (WBC, damaged, staples, pending bills, remark tabs).
  // Reco (reconciliation after the 1st cut variance): { reasons, entries }.
  getReco: (id) => api.get(`/audits/${id}/reco`).then(unwrapResponse),
  /** entries: [{ ean, reco_qty, reason, remark }]; an empty entry clears that item. */
  saveReco: (id, entries) =>
    api.put(`/audits/${id}/reco`, { entries }, { loadingMessage: 'Saving reco…' }).then(unwrapResponse),

  getAdjustments: (id) => api.get(`/audits/${id}/adjustments`).then(unwrapResponse),
  getAdjustmentRows: (id, categoryId) =>
    api.get(`/audits/${id}/adjustments/${categoryId}/rows`).then(unwrapResponse),
  addAdjustmentCategory: (id, payload) => api.post(`/audits/${id}/adjustments`, payload).then(unwrapResponse),
  updateAdjustmentCategory: (id, categoryId, payload) =>
    api.put(`/audits/${id}/adjustments/${categoryId}`, payload).then(unwrapResponse),
  deleteAdjustmentCategory: (id, categoryId) =>
    api.delete(`/audits/${id}/adjustments/${categoryId}`).then(unwrapResponse),
  clearAdjustmentRows: (id, categoryId) =>
    api.delete(`/audits/${id}/adjustments/${categoryId}/rows`).then(unwrapResponse),
  updateAdjustmentRow: (id, rowId, payload) =>
    api.put(`/audits/${id}/adjustments/rows/${rowId}`, payload).then(unwrapResponse),
  deleteAdjustmentRow: (id, rowId) =>
    api.delete(`/audits/${id}/adjustments/rows/${rowId}`).then(unwrapResponse),
  /**
   * Adds rows to an adjustment tab, in parts of 4,000 rows (request size
   * limit). replace: the first part clears the tab first.
   */
  addAdjustmentRows: async (id, categoryId, rows, { replace = false } = {}) => {
    const PART = 4000;
    const total = { added: 0, skipped: 0, warnings: [] };
    const parts = Math.max(1, Math.ceil(rows.length / PART));
    const loader = rows.length > 1 ? uploadLoader('adjustment rows', parts, rows.length) : { part() {}, done() {} };
    try {
      for (let start = 0; start < rows.length; start += PART) {
        loader.part(start / PART);
        const result = unwrapResponse(
          await api.post(`/audits/${id}/adjustments/${categoryId}/rows`, {
            rows: rows.slice(start, start + PART),
            replace: replace && start === 0,
          }),
        );
        total.added += result.added || 0;
        total.skipped += result.skipped || 0;
        total.warnings = total.warnings.concat(result.warnings || []).slice(0, 10);
      }
    } finally {
      loader.done();
    }
    return total;
  },

  getEanCorrections: (id) => api.get(`/audits/${id}/ean-corrections`).then(unwrapResponse),

  lookupEan: (id, ean) =>
    api.get(`/audits/${id}/ean-lookup?${new URLSearchParams({ ean })}`).then(unwrapResponse),

  correctEan: (id, payload) =>
    api.post(`/audits/${id}/ean-corrections`, payload).then(unwrapResponse),

  undoEanCorrection: (id, payload) =>
    api.post(`/audits/${id}/ean-corrections/undo`, payload).then(unwrapResponse),

  getSignoffs: (id) => api.get(`/audits/${id}/signoffs`).then(unwrapResponse),

  createSignoff: (id, payload) =>
    api.post(`/audits/${id}/signoffs`, payload).then(unwrapResponse),

  getBookStock: (id) => api.get(`/audits/${id}/book-stock`).then(unwrapResponse),

  deleteBookStock: (id) => api.delete(`/audits/${id}/book-stock`),

  /**
   * Sends parsed book stock rows ({ ean, book_qty, mrp, cost, article,
   * description, category }) in parts of BOOK_ROWS_PER_PART, each well under
   * the web server's 1 MB request limit. The server swaps the new stock in
   * when the last part arrives. onProgress({ part, parts }).
   */
  uploadBookStock: async (id, { rows, filename }, onProgress) => {
    const uploadId = newUploadId();
    const parts = Math.max(1, Math.ceil(rows.length / BOOK_ROWS_PER_PART));
    let result = null;
    let skipped = 0;
    const loader = uploadLoader('book stock', parts, rows.length);
    try {
      for (let index = 0; index < parts; index += 1) {
        loader.part(index);
        result = await api
          .post(`/audits/${id}/book-stock`, {
            upload_id: uploadId,
            part_index: index,
            part_count: parts,
            filename,
            rows: rows.slice(index * BOOK_ROWS_PER_PART, (index + 1) * BOOK_ROWS_PER_PART),
          })
          .then(unwrapResponse);
        skipped += result?.rows_skipped || 0;
        if (onProgress) onProgress({ part: index + 1, parts });
      }
    } finally {
      loader.done();
    }
    return { ...result, rows_skipped: skipped };
  },
};
